import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getStateForLocationPageConfig, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { FormConfigLoader } from '@ordersnproducts/modules/shared/modules/configurable-form/services/form-config-loader';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { config as _config } from '../../configs';
import { RmsFormConfig } from '../../models/rms-form-config';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';

@Injectable()
export class RmsConfigLoaderService {
  constructor(
    private store: Store<OrderBuilderState>,
    private specificationConfigLoader: FormConfigLoader,
    private featureToggle: FeatureToggleService,
  ) {}

  loadConfig() {
    return this.getConfig().pipe(
      tap((config) => {
        this.specificationConfigLoader.registerEffectsFromConfig<RmsFormConfig>(config, 'recipe');
      }),
    );
  }

  /**
   * Retrieves config for rms
   */
  getConfig(): Observable<RmsFormConfig> {
    return this.store.select(getStateForLocationPageConfig).pipe(
      take(1),
      map(({ customer, businessLineState, locationState }) => {
        const deaultExperience = ORDER_EXPERIENCES.RMX_LEGACY;
        const businessLine = businessLineState.businessLine.selected.productTypeCode;
        const deliveryMethod = businessLineState.shippingCondition.selected.shippingConditionCode;
        const orderExperience = locationState.experiences.activeExperienceCode || deaultExperience;
        const config = _safe(() => _config[customer.countryCode][businessLine][deliveryMethod][orderExperience]);
        if (!config) {
          throw new Error(`No proper config found for ${customer.countryCode}. Verify the config exists`);
        }

        return {
          ...config,
          recipe: config.recipe.filter((element) => this.hasFeatureEnabled(element)),
        };
      }),
    );
  }

  public hasFeatureEnabled(config: FieldConfig) {
    const featureEnabled = config['hasFeatureEnabled'];

    if (!!featureEnabled) {
      return this.featureToggle.isFeatureEnabled(featureEnabled);
    }

    return true;
  }

  /**
   * Retrieves form recipe config
   */
  getRecipeConfig(): Observable<FieldConfig[]> {
    return this.getConfig().pipe(map((config) => config.recipe));
  }
}
